import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, MapPin, Shield, Car, Check, ArrowRight } from "lucide-react";
import { HOME_SERVICE } from "@/data/extras";
import { THERAPIST, SERVICES, PACKAGES } from "@/data/catalog";
import useSEO from "@/hooks/useSEO";
import ReserveButton from "@/components/ReserveButton";

export default function HomeMassage() {
    useSEO({
        title: "Massage à domicile à Blainville et Rive-Nord · Jérémy Tessier",
        description: `Massothérapie à domicile dans un rayon de ${HOME_SERVICE.radiusKm} km de Blainville — même tarif clinique + ${HOME_SERVICE.surcharge} $ / séance. Forfaits sans supplément. Reçu d'assurance fourni.`,
        canonical: "https://massoclic.ca/massage-domicile",
        ogImage: "https://massoclic.ca/icon.svg",
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Massage à domicile",
            provider: {
                "@type": "HealthAndBeautyBusiness",
                name: "Jérémy Tessier — Massothérapie",
                telephone: THERAPIST.phone,
                url: "https://massoclic.ca",
            },
            areaServed: `Rive-Nord de Montréal — rayon ${HOME_SERVICE.radiusKm} km depuis Blainville`,
            description: `Même tarif que la clinique + ${HOME_SERVICE.surcharge} $ de supplément par séance à domicile.`,
        },
    });

    return (
        <main data-testid="home-massage-page" className="pt-32 md:pt-40 pb-20">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
                {/* Hero */}
                <div className="max-w-3xl mb-14">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                        <Home size={14} className="text-[#E07A5F]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                            Massage à domicile
                        </span>
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-[1.05]">
                        Je viens chez{" "}
                        <span className="italic text-[#E07A5F]">vous</span>.
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed">
                        Je me déplace dans un rayon de <b>{HOME_SERVICE.radiusKm} km autour de Blainville</b> pour un massage
                        chez vous, à votre rythme, dans le confort de votre intérieur. Table de massage professionnelle, huiles et
                        serviettes fournies — vous n&apos;avez rien à préparer.
                    </p>
                    <p className="mt-4 text-sm text-[#3D8C95] italic font-medium">
                        Reçu d&apos;assurance fourni pour chaque séance.
                    </p>
                    <div className="mt-6">
                        <ReserveButton
                            mailSubject="Massage à domicile — Demande"
                            mailBody={"Bonjour Jérémy,\n\nJe souhaite réserver un massage à domicile.\n\n• Soin souhaité : \n• Adresse complète : \n• Ville : \n• Date envisagée : \n• Heure souhaitée : \n\nMerci !"}
                            quoteSubject="Devis — Massage à domicile"
                            quoteBody={"Bonjour Jérémy,\n\nJe souhaite un devis pour un service à domicile.\n\n• Ville : \n• Type de service (individuel, couple, groupe) : \n• Date : \n• Durée souhaitée : \n\nMerci !"}
                        />
                    </div>
                </div>

                {/* Comment ça marche */}
                <section className="mb-14 grid md:grid-cols-3 gap-6">
                    {[
                        { icon: MapPin, title: `Rayon ${HOME_SERVICE.radiusKm} km`, desc: `Blainville, Boisbriand, Sainte-Thérèse, Terrebonne, Mascouche, Mirabel, Rosemère, Lorraine, Saint-Eustache…` },
                        { icon: Car, title: "Tout inclus", desc: "Table pliante professionnelle, huiles biologiques, serviettes propres, musique douce." },
                        { icon: Shield, title: "Reçu d'assurance", desc: "Fourni après chaque séance — remboursable selon votre régime." },
                    ].map((f, i) => (
                        <div key={i} className="rounded-3xl p-6 bg-white border border-[#E8E2D9]">
                            <div className="w-12 h-12 rounded-2xl bg-[#E07A5F]/10 flex items-center justify-center mb-4">
                                <f.icon size={22} className="text-[#E07A5F]" />
                            </div>
                            <h3 className="font-display text-xl text-[#2C2A29]">{f.title}</h3>
                            <p className="mt-2 text-sm text-[#6E6862] leading-relaxed">{f.desc}</p>
                        </div>
                    ))}
                </section>

                {/* Tarifs */}
                <section className="mb-14">
                    <h2 className="font-display text-2xl md:text-3xl text-[#2C2A29] mb-3">Tarifs à domicile</h2>
                    <p className="text-[#6E6862] mb-6">
                        <b>Tarif clinique + {HOME_SERVICE.surcharge} $</b> par séance à l&apos;unité (couvre le déplacement et le
                        montage/démontage sur place). <b>Forfaits sans supplément</b> — vous économisez le déplacement.
                    </p>

                    {/* Prix à l'unité (clinique + supplément) */}
                    <div className="rounded-3xl bg-white border border-[#E8E2D9] p-6 md:p-8 mb-6">
                        <h3 className="font-display text-xl text-[#2C2A29] mb-4">À la séance</h3>
                        <div className="space-y-3">
                            {SERVICES.flatMap((s) =>
                                s.variants.map((v) => (
                                    <div
                                        key={v.id}
                                        data-testid={`home-price-${v.id}`}
                                        className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-[#E8E2D9] last:border-b-0"
                                    >
                                        <div>
                                            <div className="text-sm text-[#2C2A29] font-medium">
                                                {s.name} · {v.label}
                                            </div>
                                            <div className="text-xs text-[#6E6862]">
                                                Clinique {v.price} $ + {HOME_SERVICE.surcharge} $ déplacement
                                            </div>
                                        </div>
                                        <div className="font-display text-xl text-[#2C2A29]">
                                            {v.price + HOME_SERVICE.surcharge} <span className="text-sm text-[#6E6862]">$</span>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Forfaits à domicile (sans supplément) */}
                    <div className="rounded-3xl bg-[#3D8C95] text-white p-6 md:p-8">
                        <div className="flex items-center gap-2 mb-4">
                            <Check size={20} className="text-white" />
                            <h3 className="font-display text-xl">Forfaits à domicile — sans supplément</h3>
                        </div>
                        <p className="text-sm text-white/80 mb-5">
                            En achetant un forfait, le déplacement est offert. Même prix qu&apos;en clinique.
                        </p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {PACKAGES.map((p) => (
                                <div
                                    key={p.id}
                                    data-testid={`home-package-${p.id}`}
                                    className="rounded-2xl p-4 bg-white/10 border border-white/20"
                                >
                                    <div className="text-xs uppercase tracking-widest text-white/70">
                                        {p.count} massages
                                    </div>
                                    <div className="mt-2 font-display text-3xl">
                                        {p.price}
                                        <span className="text-sm text-white/80"> $</span>
                                    </div>
                                    <div className="text-xs text-white/70 mt-1">
                                        Économie {p.save} $
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <div className="rounded-3xl bg-[#2C2A29] text-white p-8 md:p-12 text-center">
                    <h2 className="font-display text-3xl md:text-4xl leading-tight">
                        Réservez votre séance à domicile
                    </h2>
                    <p className="mt-3 text-white/75 max-w-2xl mx-auto leading-relaxed">
                        Contactez-moi par courriel ou téléphone en précisant votre adresse — je confirme la disponibilité dans les 24 h.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <a href={`mailto:${THERAPIST.email}?subject=${encodeURIComponent("Réservation à domicile")}`}>
                            <Button
                                data-testid="home-mail-cta"
                                className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6 text-base"
                            >
                                Réserver par courriel <ArrowRight size={16} className="ml-2" />
                            </Button>
                        </a>
                        <a href={`tel:${THERAPIST.phone}`}>
                            <Button variant="outline" className="rounded-full border-white text-white hover:bg-white hover:text-[#2C2A29] px-8 py-6 text-base">
                                {THERAPIST.phone}
                            </Button>
                        </a>
                    </div>
                </div>

                <div className="mt-10 text-center">
                    <Link to="/" className="text-sm text-[#3D8C95] hover:text-[#2C6D75]">
                        ← Retour à l&apos;accueil
                    </Link>
                </div>
            </div>
        </main>
    );
}
